import CommentComponent from "./Comment";
import { ClientProcess, ClientProcessComment } from "@avenueops/shared-types";
import { CommentType, Role } from "@prisma/client";
import { useMembership } from "context/OrganizationContext";
import { useState } from "react";

interface Props {
  process: ClientProcess;
  small?: boolean;
}

enum CommentListSort {
  ALL,
  PUBLIC,
  INTERNAL,
}

export function CommentList({ process, small }: Props) {
  const membership = useMembership();
  const [selectedSort, setSelectedSort] = useState<CommentListSort>(CommentListSort.ALL);

  const getClassName = (filter: CommentListSort) => {
    if (filter === selectedSort) {
      return "px-3 py-1 border border-cool-gray-300 rounded-full font-medium text-gray-950 cursor-pointer bg-white";
    } else {
      return "px-3 py-1 text-gray-500 border border-cool-gray-50 rounded-full hover:border-cool-gray-300 cursor-pointer";
    }
  };

  const sortedAllComments: ClientProcessComment[] =
    process.comments.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }) ?? [];
  const sortedInternalComments = sortedAllComments.filter((comment) => comment.commentType === CommentType.INTERNAL);
  const sortedPublicComments = sortedAllComments.filter((comment) => comment.commentType === CommentType.PUBLIC);

  const internalCommentCount = sortedInternalComments.length;
  const publicCommentCount = sortedPublicComments.length;
  const totalCommentCount = internalCommentCount + publicCommentCount;

  return (
    <>
      <div className="flex flex-row gap-20 items-center mt-4">
        <div className={small ? "font-medium text-sm" : "font-bold text-2xl"}>Comments</div>
        {membership && (membership.role === Role.CREATOR || membership.role === Role.ADMIN) ? (
          <div className="flex flex-row gap-5 text-sm items-center whitespace-nowrap">
            <div className={getClassName(CommentListSort.ALL)} onClick={() => setSelectedSort(CommentListSort.ALL)}>
              All ({totalCommentCount})
            </div>
            <div className={getClassName(CommentListSort.PUBLIC)} onClick={() => setSelectedSort(CommentListSort.PUBLIC)}>
              Public ({publicCommentCount})
            </div>
            <div className={getClassName(CommentListSort.INTERNAL)} onClick={() => setSelectedSort(CommentListSort.INTERNAL)}>
              Internal ({internalCommentCount})
            </div>
          </div>
        ) : null}
      </div>

      {totalCommentCount === 0 ? (
        <div className="text-sm text-gray-700 font-base pt-10 flex justify-center items-center">There are no comments</div>
      ) : membership && (membership.role === Role.CREATOR || membership.role === Role.ADMIN) ? (
        selectedSort === CommentListSort.ALL ? (
          <>
            {sortedAllComments.map((comment) => (
              <CommentComponent key={comment.id} comment={comment} small={small} />
            ))}
          </>
        ) : selectedSort === CommentListSort.PUBLIC ? (
          <>
            {sortedPublicComments.map((comment) => (
              <CommentComponent key={comment.id} comment={comment} small={small} />
            ))}
          </>
        ) : (
          <>{sortedInternalComments?.map((comment) => <CommentComponent key={comment.id} comment={comment} small={small} />)}</>
        )
      ) : (
        <>{sortedPublicComments?.map((comment) => <CommentComponent key={comment.id} comment={comment} small={small} />)}</>
      )}
    </>
  );
}
